/* The curtain drawn across a language change.

   Switching between English and Arabic is not a text substitution — it is a
   mirror. Every row reverses, the header's back chevron turns around, prices
   move to the other end of their line, and any animation caught mid-flight
   restarts from a new origin. Shown live, the eye reads that as the page
   breaking and re-assembling itself.

   So the swap is covered, and the cover does three jobs at once:

   · It hides a reflow that has no attractive form. There is no way to tween
     between two mirrored layouts; the honest answer is not to show it.
   · It names the language being moved to, in that language's own script. The
     one word still readable to the person pressing the button is the word
     they pressed.
   · It withdraws in the *new* reading direction. Arabic is uncovered from the
     right, English from the left, so the motion states the change rather than
     merely concealing it.

   The withdrawal is a translate on a single full-screen layer, so the whole
   sequence is one composited element — the app underneath is never asked to
   re-paint for the sake of the animation. The frosted material travels with
   it, which is what makes the app sharpen progressively behind the trailing
   edge instead of snapping back into focus all at once.

   Phase, and therefore timing, is owned by state/prefs: the cover has to be
   complete before the strings change, and only the code performing the change
   knows when that is. This file draws what it is told. */

import { createPortal } from 'react-dom'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { isRtl, languageOf } from '../../i18n'
import { EASE_DRAWER, EASE_OUT } from '../../design/motion'
import { LOCALE_COVER_MS, LOCALE_REVEAL_MS, usePrefs } from '../../state/prefs'

export function LocaleSwap() {
  const { t } = useTranslation()
  const { localeSwap } = usePrefs()
  const reduce = useReducedMotion()

  if (typeof document === 'undefined') return null

  const { phase, to } = localeSwap
  const language = to ? languageOf(to) : null
  const leaving = phase === 'revealing'

  /* Where the curtain goes when it leaves. It uncovers the edge the new
     language is read *from*, so Arabic pulls away to the left and English to
     the right — each one the opposite of its own reading direction. */
  const towardsRtl = Boolean(to && isRtl(to))
  const exitX = towardsRtl ? '-102%' : '102%'

  return createPortal(
    <AnimatePresence>
      {phase !== 'idle' && language && (
        <motion.div
          key="locale-veil"
          className="locale-veil"
          /* `alert`, not `status`: it is transient, and the message is the
             entire content of the region rather than an update inside a
             larger one, so it should be read at once rather than queued. */
          role="alert"
          initial={{ x: 0, opacity: 0 }}
          animate={
            leaving
              ? /* Under reduced motion the curtain has nowhere to go, so it
                   dissolves in place. The swap is still hidden — that part is
                   not decoration and does not get dropped. */
                reduce
                ? { x: 0, opacity: 0 }
                : { x: exitX, opacity: 1 }
              : { x: 0, opacity: 1 }
          }
          exit={{ opacity: 0, transition: { duration: 0.001 } }}
          transition={{
            duration: (leaving ? LOCALE_REVEAL_MS : LOCALE_COVER_MS) / 1000,
            ease: leaving ? EASE_DRAWER : EASE_OUT,
            ...(reduce ? { duration: 0.09, ease: EASE_OUT } : {}),
          }}
        >
          {/* The leading edge: a hairline of brand colour on the side that
              travels, so the curtain reads as drawn aside rather than as a
              rectangle dissolving. It sits on the trailing side of the
              motion, which is the one still over the page. */}
          {!reduce && (
            <span className={`locale-veil-edge ${towardsRtl ? 'is-start' : 'is-end'}`} aria-hidden />
          )}

          <motion.div
            className="locale-veil-word"
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 10, scale: 0.96 }}
            /* The word fades where it stands rather than riding off with the
               curtain, and it goes first: the last thing the user sees before
               the app returns is the language name settling, not a label
               sliding away. */
            animate={leaving ? { opacity: 0, y: 0, scale: 1 } : { opacity: 1, y: 0, scale: 1 }}
            transition={
              leaving
                ? { duration: 0.14, ease: EASE_OUT }
                : { duration: 0.34, ease: EASE_OUT, delay: reduce ? 0 : 0.04 }
            }
          >
            {/* `lang` and `dir` are what make the browser reach for the Arabic
                face for العربية and the Latin one for English inside a
                document currently set to the other. Without them this word
                renders through the wrong font's fallback — the one place in
                the app where that would be unmissable. */}
            <span className="locale-veil-name" lang={language.code} dir={language.dir}>
              {language.name}
            </span>
            <span className="sr-only">{t('region.switching')}</span>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
